import React, { useId, useState } from "react";

import { ScreenIntro } from "../PatternSections";
import DiagnosisStats from "./DiagnosisStats";
import HistoryConfirm from "./HistoryConfirm";
import SessionContext from "./SessionContext";
import TrackRecord from "./TrackRecord";
import useSessionContext, { sessionIdOf } from "./useSessionContext";
import { HISTORY_SOURCE, HISTORY_STATUS, HISTORY_TEXT, fullWhen } from "./catalogText";
import { labelOf } from "./useHistory";

const T = HISTORY_TEXT;
const D = T.detail;

/** Outcome, source, status (with the undo reason and time) and the case id as one compact label / value list. */
function Record({ item, options }) {
  const statusKey = item.undone ? HISTORY_STATUS.UNDONE : HISTORY_STATUS.ACTIVE;
  const sourceKey = item.manual ? HISTORY_SOURCE.MANUAL : HISTORY_SOURCE.AUTO;
  const undoneNote = item.undone
    ? [item.reason && labelOf(options.undo_reasons, item.reason), item.undone_at && T.row.undoneAt(fullWhen(item.undone_at))].filter(Boolean).join(" ")
    : "";
  return (
    <div className="le-field">
      <span className="le-label">{D.record}</span>
      <dl className="le-defs">
        <dt>{D.outcome}</dt><dd className="le-cause-name">{labelOf(options.kinds, item.kind)}</dd>
        <dt>{D.source}</dt><dd>{labelOf(T.sources, sourceKey)}</dd>
        <dt>{D.status}</dt>
        <dd>
          <span className={`le-tag${item.undone ? " le-tag--muted" : ""}`}>{labelOf(options.statuses, statusKey)}</span>
          {undoneNote && <span className="le-muted le-defs-note">{undoneNote}</span>}
        </dd>
        <dt>{D.eid}</dt><dd><code>{item.eid}</code></dd>
      </dl>
    </div>
  );
}

/**
 * The detail popup's body for one learned case, laid out like the Learning editor's Review screen: the cause as the title with
 * product · category and when as its meta line (manual / found by Arta, escalated and undone as tags), how the conversation
 * started (SessionContext), the cause's track record with this case's weight in it (TrackRecord, from the page's `causes`), the
 * rough figures and timeline of the diagnosis (DiagnosisStats; one request shared with SessionContext), the record, and Undo /
 * Redo through the preview-and-confirm flow (HistoryConfirm) where the server allows it. `onDone(action, result)` runs after
 * the server answered.
 */
export default function HistoryDetail({ item, options, causes, onDone }) {
  const id = useId();
  const [confirming, setConfirming] = useState(false);
  const context = useSessionContext(sessionIdOf(item));
  const category = [item.product, item.category_label || item.category].filter(Boolean).join(" · ");
  const tags = [
    item.manual ? { label: T.badges.manual, tone: "warn", hint: T.badges.manualHint } : { label: T.badges.auto, tone: "muted" },
    item.escalated && { label: T.badges.escalated, tone: "warn" },
    item.undone && { label: labelOf(options.statuses, HISTORY_STATUS.UNDONE), tone: "muted" },
  ];
  return (
    <section className="le-screen" aria-labelledby={`${id}-h`}>
      <ScreenIntro id={`${id}-h`} heading={item.cause_label} meta={D.meta(category, fullWhen(item.when))} tags={tags} />
      <SessionContext context={context} />
      <TrackRecord item={item} causes={causes} />
      <DiagnosisStats context={context} />
      <Record item={item} options={options} />

      {!item.can_undo && <p className="le-muted">{`${T.row.readOnly}. ${T.row.readOnlyHint}`}</p>}
      {item.can_undo && (confirming
        ? <HistoryConfirm item={item} reasons={options.undo_reasons} onDone={onDone} onCancel={() => setConfirming(false)} />
        : <div className="le-actions"><button type="button" className="le-btn" onClick={() => setConfirming(true)}>{item.undone ? T.row.redo : T.row.undo}</button></div>)}
    </section>
  );
}
